import { Box, Divider } from '@mui/material';
import { useSearch } from '@tanstack/react-router';
import { endOfMonth, startOfMonth } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateObject, toDateObject } from 'react-multi-date-picker';
import Page from 'src/components/organisms/page/page';
import { useSortController } from 'src/hooks/use-sort-controller';
import { withPermissions } from 'src/permissions/can';
import { SortOrder } from 'src/types/enum/sort-order';
import { getSelectedFilters } from 'src/utils/filters/get-selected-filters';
import { useBookingStatistics } from '../../api/hooks/use-booking-statistics';
import { useFilteredAndSortedStatisticsData } from '../../hooks/use-filtered-and-sorted-statistics-data';
import { useStatisticsDateRangeController } from '../../hooks/use-statistics-date-range-controller';
import { ContentType } from '../../types/content-type';
import { ContentView } from '../../types/content-view';
import { StatisticsVariant } from '../../types/statistic-variant';
import { getMinMaxCalendarDates } from '../../utils/calendar';
import {
  isContentType,
  isContentView,
  isDateRange,
  isStatisticVariant,
} from '../../utils/filters';
import { groupBookingStatistics } from '../../utils/group-booking-statistics';
import DateRangeSelect from '../molecules/date-range-select/date-range-select';
import { ContentViewFilterSelect } from '../molecules/filters/content-view-filter-select';
import { FloorsFilterPopover } from '../molecules/filters/floors-filter-popover';
import { StatisticsFilterChips } from '../molecules/filters/statistics-filter-chips';
import { TypesFilterSelect } from '../molecules/filters/types-filter-select';
import { StatisticVariantFilterSelect } from '../molecules/filters/variant-filter-select';
import StatisticsLineChart from '../organisms/statistics-line-chart';
import StatisticsSummaryPanel from '../organisms/statistics-summary-panel';
import StatisticsTable from '../organisms/statistics-table';

const Statistics = () => {
  const { t } = useTranslation(['statistics', 'common']);

  const { filter } = useSearch({
    from: '/_layout/tenant/statistics/_layout/',
    select(state) {
      return {
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  //sort & filter state except floor filter
  const [sortController, setSortController] = useSortController({
    sortBy: 'date',
    sortOrder: SortOrder.ASC,
  });

  const [variant, setVariant] = useState<StatisticsVariant>(
    isStatisticVariant(getSelectedFilters(filter, 'variant')?.[0])
      ? (getSelectedFilters(filter, 'variant')?.[0] as StatisticsVariant)
      : 'MONTHLY',
  );

  const [contentType, setContentType] = useState<ContentType>(
    isContentType(getSelectedFilters(filter, 'contentType')?.[0])
      ? (getSelectedFilters(filter, 'contentType')?.[0] as ContentType)
      : 'GENERAL',
  );

  const [contentView, setContentView] = useState<ContentView>(
    isContentView(getSelectedFilters(filter, 'contentView')?.[0])
      ? (getSelectedFilters(filter, 'contentView')?.[0] as ContentView)
      : 'TABLE',
  );

  const [dateRange, setDateRange] = useState<DateObject[] | null>(
    isDateRange(getSelectedFilters(filter, 'range')?.[0])
      ? [
          new DateObject(
            getSelectedFilters(filter, 'range')[0].split(',')[0].split('T')[0],
          ),
          new DateObject(
            getSelectedFilters(filter, 'range')[0].split(',')[1].split('T')[0],
          ),
        ]
      : variant === 'MONTHLY'
        ? [
            new DateObject(startOfMonth(new Date())),
            new DateObject(endOfMonth(new Date())),
          ]
        : null,
  );

  //   const [
  //     isOpenExportBookingsAsXlsDialog,
  //     _handleOpenExportBookingsAsXlsDialog,
  //     handleCloseExportBookingsAsXlsDialog,
  //   ] = useDialog();

  const { from, to } = useStatisticsDateRangeController(dateRange, variant);

  const { data, isLoading, error, handleChangeFilterValue, handleChangeSort } =
    useBookingStatistics(from, to, variant, !!from && !!to);

  const { grouped, floors } = groupBookingStatistics(
    data?.dates || [],
    variant,
    contentView,
  );

  const sortedData = useFilteredAndSortedStatisticsData(
    grouped,
    sortController.sortBy,
    sortController.sortOrder,
    contentType,
    filter,
  );

  //helper - minMaxCalendarDates:

  const { minDate, maxDate } = useMemo(() => {
    return getMinMaxCalendarDates(variant, dateRange);
  }, [variant, dateRange]);

  const didMountRef = useRef(false);

  //reset floors
  useEffect(() => {
    if (didMountRef.current) {
      handleChangeFilterValue('floors', []);
    } else {
      didMountRef.current = true;
    }
  }, [data]);

  useEffect(() => {
    if (!from || !to) {
      handleChangeFilterValue('range', []);
    } else {
      handleChangeFilterValue('range', [`${from},${to}`]);
    }
  }, [from, to]);

  return (
    <Page>
      <Page.Header title={t('statistics:common.title')}>
        <StatisticVariantFilterSelect
          filters={filter}
          handleChangeFilterValue={handleChangeFilterValue}
          variant={variant}
          setVariant={setVariant}
          setDateRange={setDateRange}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}>
          <DateRangeSelect
            value={dateRange || []}
            numberOfMonths={variant === 'DAILY' ? 2 : 1}
            calendarPosition="bottom-center"
            onlyMonthPicker={variant === 'MONTHLY'}
            headerOrder={
              variant === 'MONTHLY'
                ? ['YEAR_MONTH', 'LEFT_BUTTON', 'RIGHT_BUTTON']
                : undefined
            }
            minDate={minDate}
            maxDate={maxDate}
            onChange={range => {
              if (
                variant === 'MONTHLY' &&
                range.length > 1 &&
                range[1] instanceof DateObject &&
                range[1].isValid
              ) {
                const normalizedEndDate = endOfMonth(range[1].toDate());
                const normalizedEndDateObject = toDateObject(normalizedEndDate);
                setDateRange([range[0], normalizedEndDateObject]);
              } else setDateRange(range);
            }}
          />
          {/* TODO: Add download functionality when api is ready */}
          {/* <Can I="download" a="Statistics" passThrough>
            {allowed => (
              <Tooltip
                title={t('common:notSufficientPermissions')}
                disableHoverListener={allowed}
                disableFocusListener={allowed}>
                <span>
                  <Button
                    //   onClick={handleOpenExportStatisticsAsXlsDialog}
                    color="primary"
                    disabled={!allowed}
                    startIcon={<DownloadIcon fontSize="small" />}
                    size="large"
                    variant="contained"
                    id="download_statistics_data_button">
                    {t('statistics:common.downloadButtonText')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Can> */}
        </Box>
      </Page.Header>
      <StatisticsSummaryPanel
        data={[
          {
            type: 'TOTAL',
            count: data?.total || 0,
          },
          {
            type: 'CONFIRMED',
            count: data?.confirmed || 0,
          },
          {
            type: 'UNCONFIRMED',
            count: data?.unconfirmed || 0,
          },
          {
            type: 'FINISHED_EARLY',
            count: data?.finishedEarly || 0,
          },
          {
            type: 'FINISHED',
            count: data?.finished || 0,
          },
          {
            type: 'CANCELLED',
            count: data?.cancelled || 0,
          },
        ]}
      />
      <Page.ContentContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            pt: 2.5,
            pb: 2,
          }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}>
            <TypesFilterSelect
              filters={filter}
              handleChangeFilterValue={handleChangeFilterValue}
              contentType={contentType}
              setContentType={setContentType}
            />
            <ContentViewFilterSelect
              filters={filter}
              handleChangeFilterValue={handleChangeFilterValue}
              handleChangeSort={handleChangeSort}
              contentView={contentView}
              setContentView={setContentView}
              setSortController={setSortController}
            />
          </Box>
          {grouped?.length > 0 && (
            <FloorsFilterPopover
              filters={filter}
              handleChangeFilterValue={handleChangeFilterValue}
              items={floors.map(floor => ({
                value: floor.toString(),
                label: floor.toString(),
              }))}
            />
          )}
        </Box>
        <StatisticsFilterChips
          filters={filter}
          handleChangeFilterValue={handleChangeFilterValue}
        />
        {contentView === 'TABLE' ? (
          <StatisticsTable
            data={sortedData.data}
            isLoading={isLoading}
            error={error?.response?.data?.message}
            sortController={sortController}
            setSortController={setSortController}
            handleChangeSort={handleChangeSort}
            contentType={contentType}
            variant={variant}
          />
        ) : (
          <>
            <Divider sx={{ mb: 2.5 }} />
            <StatisticsLineChart
              data={sortedData.data}
              error={error?.response?.data?.message}
              isLoading={isLoading}
              variant={variant}
              contentType={contentType}
            />
          </>
        )}
        {/* <ExportStatisticsAsXlsDialog
        open={isOpenExportBookingsAsXlsDialog}
        onCancel={handleCloseExportBookingsAsXlsDialog}
      /> */}
      </Page.ContentContainer>
    </Page>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'Statistics'),
  Statistics,
);
